<template>
  <div
    class="h-screen overflow-y-auto overscroll-contain bg-yapgray-100/80 p-4 pb-36 backdrop-blur firefox:bg-yapgray-100 dark:bg-yapdarkgray-900/80 firefox:dark:bg-yapdarkgray-900"
  >
    <div class="flex h-20 w-full items-center rounded-t-lg bg-white p-4 dark:bg-yapdarkgray-800">
      <div class="flex w-full">
        <AccountEntry
          v-if="state.currentContextSwitch"
          :name="state.currentContextSwitch.name"
          currency="CHF"
          :amount="cashAccountsStore.cashAccountsBalanceForCurrentContext"
          :picture-uri="state.currentContextSwitch.pictureUri"
          :can-hide-amount="true"
        />
        <ContextSwitcher />
      </div>
    </div>

    <nav id="nav" class="relative rounded-b-lg bg-white dark:bg-yapdarkgray-800">
      <div v-if="!userStore.isIdentOnlyQES" class="mt-1 flex flex-col p-2">
        <h4 class="text-xs text-yapgray-500 dark:text-yapgray-200">
          {{ t('webapp.nav.sideMenu.mainMenu.title') }}
        </h4>
        <template v-for="item in state.navItems" :key="item.id">
          <router-link
            v-if="'routeName' in item"
            :to="{ name: item.routeName }"
            class="group flex items-center justify-between rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-50 dark:hover:text-primary-dark-200"
            @click="$emit('on-navigate')"
          >
            <div class="flex gap-2">
              <YapIcon
                :name="item.icon"
                class="h-6 w-6 group-hover:text-primary-500 dark:text-yapgray-50 dark:group-hover:text-primary-dark-300"
              />
              <span>{{ t(item.title) }}</span>
            </div>

            <YapBadge :value="item.badge" :badge-style="BadgeStyle.Default" rounded v-if="item.badge" />
          </router-link>
          <div
            v-else-if="'action' in item"
            class="group flex cursor-pointer items-center justify-between rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-50 dark:hover:text-primary-dark-300"
            @click="item.action"
          >
            <div class="flex gap-2">
              <YapIcon
                :name="item.icon"
                class="h-6 w-6 group-hover:text-primary-500 dark:text-yapgray-50 dark:group-hover:text-primary-dark-300"
              />
              <span>{{ t(item.title) }}</span>
            </div>

            <YapBadge :value="item.badge" :badge-style="BadgeStyle.Default" rounded v-if="item.badge" />
          </div>
          <pre v-else>else</pre>
        </template>
      </div>
      <div class="mt-1 flex flex-col p-2" v-if="state.pendingNavLinks.length">
        <h4 class="text-xs text-yapgray-500">{{ t('webapp.nav.sideMenu.pending.title') }}</h4>
        <router-link
          v-for="link in state.pendingNavLinks"
          :key="link.routeName"
          :to="{ name: link.routeName }"
          class="group rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-50 dark:hover:text-primary-dark-300"
          @click="$emit('on-navigate')"
        >
          <div class="flex items-center gap-2">
            <YapIcon
              :name="link.icon"
              class="h-6 w-6 min-w-[24px] group-hover:text-primary-500 dark:text-primary-dark-100 dark:group-hover:text-primary-dark-300"
            />
            <span>{{ t(link.title) }}</span>
          </div>
        </router-link>
      </div>
      <div class="mt-1 flex flex-col p-2" v-if="state.otherNavLinks.length">
        <h4 class="text-xs text-yapgray-500 dark:text-yapgray-200">{{ $t('webapp.nav.sideMenu.org.title') }}</h4>
        <router-link
          v-for="link in state.otherNavLinks"
          :key="link.routeName"
          :to="{ name: link.routeName }"
          class="group rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-50 dark:hover:text-primary-dark-300"
          @click="$emit('on-navigate')"
        >
          <YapTooltip :text="link.tooltip" :style="TooltipStyle.Dark" :width="TooltipWidth.Small" :offset-pos="25">
            <div class="flex items-center gap-2">
              <YapIcon
                :name="link.icon"
                class="h-6 w-6 min-w-[24px] group-hover:text-primary-500 dark:text-yapgray-50 dark:group-hover:text-primary-dark-300"
              />
              <span>{{ $t(link.title) }}</span>
            </div>
          </YapTooltip>
        </router-link>
      </div>
    </nav>

    <OtherAccounts />

    <div
      v-if="getEnv('VITE_APP_ENV') === 'local' || getEnv('VITE_APP_ENV') === 'sandbox'"
      class="my-10 rounded-lg border-2 border-teal-500 p-2 font-mono"
    >
      <h4 class="text-xs text-yapgray-500">Debug - Only for internal testing</h4>
      <router-link
        :to="{ name: RouteNames.InputTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Input testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.ButtonTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Button testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.BadgeTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Badge testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.AlertTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Alert testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.MaskedInputTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Masked Input testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.YapPageTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        YapPage testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.ColorTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Colors Testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.ComponentTesting }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Components testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.YapFileResourceUploader }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        File Resource testing
      </router-link>
      <router-link
        :to="{ name: RouteNames.Suggestions }"
        class="flex items-center gap-2 rounded-lg p-2 py-4 text-sm text-yapgray-900 hover:text-primary-500 dark:text-yapgray-200 dark:hover:text-primary-dark-300"
        @click="$emit('on-navigate')"
      >
        Suggestions testing
      </router-link>
    </div>
  </div>
  <ScanPaymentModal :set-is-open="state.isScanPaymentModalOpen" @modal-closed="state.isScanPaymentModalOpen = false" />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, watch } from 'vue';
import { RouteLocationNormalized, useRoute } from 'vue-router';

import getEnv from '@/utils/env';
import apiGw from '@/services/apiGw';
import { useI18n } from '@/plugins/i18n';
import { BadgeStyle } from '@/types/yapBadge';
import YapIcon from '@/components/YapIcon.vue';
import YapBadge from '@/components/YapBadge.vue';
import { useUserStore } from '@/store/userStore';
import { RouteNames, routes } from '@/router/routes';
import OtherAccounts from '@/components/OtherAccounts.vue';
import ContextSwitcher from '@/components/ContextSwitcher.vue';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import AccountEntry from '@/components/sideMenu/AccountEntry.vue';
import { Partnership, PartnershipStatus } from '@/types/partnership';
import { createResourceUri, getUidFromSelfLink } from '@/utils/links';
import usePaymentEntitlement from '@/composables/usePaymentEntitlement';
import { useOpenPaymentsStatsStore } from '@/store/openPaymentsStatsStore';
import { useSuggestionsStore } from '@/store/suggestionsStore';
import { ContextSwitch, useContextSwitchStore } from '@/store/contextSwitchStore';
import YapTooltip, { TooltipStyle, TooltipWidth } from '@/components/YapTooltip.vue';
import ScanPaymentModal from '@/components/payments/ScanPaymentModal.vue';
import router from '@/router/router';
import { NaturalPersonURI } from '@/types/naturalPerson';

const { t } = useI18n();
const userStore = useUserStore();
const partnershipsStore = usePartnershipsStore();
const cashAccountsStore = useCashAccountsStore();
const contextSwitchStore = useContextSwitchStore();
const openPaymentsStatsStore = useOpenPaymentsStatsStore();

const { openPaymentsCount } = storeToRefs(openPaymentsStatsStore);

const { canCreatePaymentOnSelectedCashAccount } = usePaymentEntitlement();

const emit = defineEmits(['on-navigate']);

interface NavItem {
  id: string;
  title: string;
  icon: string;
  displayCondition?: boolean | null;
  tooltip?: string;
  badge?: string | number;
}

interface NavAction extends NavItem {
  action: () => void;
}

interface NavLink extends NavItem {
  routeName: RouteNames;
}

interface State {
  currentRoute: RouteLocationNormalized;
  searchQuery: string;
  switchingContext: boolean;
  navItems: Array<NavLink | NavAction>;
  pendingNavLinks: Array<NavLink>;
  otherNavLinks: Array<NavLink>;
  currentContextSwitch?: ContextSwitch;
  currentPartnership?: Partnership;
  hasActivateEbillSuggestion: boolean;
  isScanPaymentModalOpen: boolean;
  canCreatePayment: boolean | null;
}

const state: State = reactive({
  currentRoute: useRoute(),
  searchQuery: '',
  switchingContext: false,
  navItems: computed(() => {
    const navItems = [
      { id: RouteNames.Dashboard, routeName: RouteNames.Dashboard, title: 'Dashboard', icon: 'home-outline' },
      {
        id: RouteNames.NewPayment,
        routeName: RouteNames.NewPayment,
        title: 'app.payments.newPayment',
        icon: 'arrow-path-outline',
        displayCondition: state.currentPartnership?.status !== PartnershipStatus.Screened && state.canCreatePayment
      },
      {
        id: 'triggerScanPaymentModal',
        action: () => triggerScanPaymentModal(),
        title: 'app.home.navigation.scanPayment',
        icon: 'QR-payment',
        displayCondition: state.currentPartnership?.status !== PartnershipStatus.Screened && state.canCreatePayment
      },
      {
        id: RouteNames.Transactions,
        routeName: RouteNames.Transactions,
        title: 'app.transactions.transactions',
        icon: 'wallet-outline',
        displayCondition:
          state.currentPartnership?.status !== PartnershipStatus.Identified &&
          state.currentPartnership?.status !== PartnershipStatus.Screened
      },
      {
        id: RouteNames.AccountInfo,
        routeName: RouteNames.AccountInfo,
        title: 'app.home.navigation.accountInfo',
        icon: 'accountInfo',
        displayCondition:
          state.currentPartnership?.status !== PartnershipStatus.Identified &&
          state.currentPartnership?.status !== PartnershipStatus.Screened &&
          cashAccountsStore.entitledCashAccountsForCurrentContext.length !== 0
      },
      {
        id: RouteNames.Cards,
        routeName: RouteNames.Cards,
        title: 'app.home.navigation.cardSettings',
        icon: 'card-outline',
        displayCondition:
          state.currentPartnership?.status !== PartnershipStatus.Identified &&
          state.currentPartnership?.status !== PartnershipStatus.Screened
      },
      {
        id: RouteNames.CardsAdmin,
        routeName: RouteNames.CardsAdmin,
        title: 'webapp.nav.main.cardAdmin',
        icon: 'card-outline',
        displayCondition:
          state.currentPartnership?.status !== PartnershipStatus.Identified &&
          state.currentPartnership?.status !== PartnershipStatus.Screened
      },
      {
        id: RouteNames.PaymentOverview,
        routeName: RouteNames.PaymentOverview,
        title: 'webapp.nav.main.paymentOverview',
        icon: 'stack',
        badge: openPaymentsCount.value,
        displayCondition:
          state.currentPartnership?.status !== PartnershipStatus.Identified &&
          state.currentPartnership?.status !== PartnershipStatus.Screened
      },
      {
        id: RouteNames.ActivateEbill,
        routeName: RouteNames.ActivateEbill,
        title: 'app.home.navigation.activateEbill',
        icon: 'eBill',
        displayCondition: state.hasActivateEbillSuggestion
      },
      {
        id: RouteNames.EbillBillers,
        routeName: RouteNames.EbillBillers,
        title: 'app.home.navigation.ebillers',
        icon: 'eBill',
        displayCondition: state.hasActivateEbillSuggestion
          ? false
          : contextSwitchStore.isPrivateContext
            ? userStore.hasEbillActivated
            : partnershipsStore.hasEbillActivatedForCurrentPartnership
      },
      {
        id: RouteNames.NoActiveRoles,
        routeName: RouteNames.NoActiveRoles,
        title: 'app.partnership.home.requestorOptions.title',
        icon: 'users-outline',
        displayCondition: partnershipsStore.isRequestorBootstrapperForCurrentPartnership
      },
      {
        id: RouteNames.ComplianceDeclaration,
        routeName: RouteNames.ComplianceDeclaration,
        title: 'app.settings.complianceDeclaration.title',
        icon: 'company',
        displayCondition:
          state.currentPartnership?.status === PartnershipStatus.Identified ||
          state.currentPartnership?.status === PartnershipStatus.Screened ||
          state.currentPartnership?.status === PartnershipStatus.Bankable
      },
      {
        id: RouteNames.TeamSpace,
        routeName: RouteNames.TeamSpace,
        title: 'app.partnership.overview.management.delegates',
        icon: 'users-outline',
        displayCondition: state.currentPartnership?.status === PartnershipStatus.Bankable
      }
    ];
    return filterNavItems(navItems);
  }),
  pendingNavLinks: computed(() => {
    const pendingLinks = [
      {
        id: RouteNames.PartnershipDetails,
        routeName: RouteNames.PartnershipDetails,
        title: 'webapp.nav.pending.update-partnership',
        icon: 'pencil-outline',
        displayCondition:
          contextSwitchStore.isInternalRegistryPartnership && partnershipsStore.getPendingUpdatePartnership
      }
    ];
    return filterLinks(pendingLinks);
  }),
  otherNavLinks: computed(() => {
    const otherLinks = [
      {
        id: RouteNames.ViaApp,
        routeName: RouteNames.ViaApp,
        title: 'webapp.nav.org.viaApp',
        icon: 'registration-outline',
        displayCondition: contextSwitchStore.isPrivateContext,
        tooltip: t('webapp.nav.org.viaApp-tooltip')
      },
      {
        id: RouteNames.AccountConfirmations,
        routeName: RouteNames.AccountConfirmations,
        title: 'webapp.nav.org.qes',
        icon: 'signature-outline',
        displayCondition: contextSwitchStore.isPrivateContext,
        tooltip: t('webapp.nav.org.qes-tooltip')
      },
      {
        id: RouteNames.Registrations,
        routeName: RouteNames.Registrations,
        title: 'webapp.nav.org.internal',
        icon: 'registration-outline',
        displayCondition: userStore.hasPartnershipDeclarations && contextSwitchStore.isPrivateContext,
        tooltip: t('webapp.nav.org.internal-tooltip')
      }
    ];
    return filterLinks(otherLinks);
  }),
  currentContextSwitch: computed(() => contextSwitchStore.getCurrentContext()),
  currentPartnership: computed(() => partnershipsStore.currentPartnership),
  hasActivateEbillSuggestion: computed(() => suggestionsStore.hasActivateEbillSuggestion()),
  isScanPaymentModalOpen: false,
  canCreatePayment: computed(() => canCreatePaymentOnSelectedCashAccount())
});

watch(
  () => userStore.email,
  (newVal, oldVal) => {
    if (newVal && oldVal !== newVal) {
      loadRequiredData();
    }
  }
);

watch(
  () => contextSwitchStore.currentContextUid,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadRequiredData();

      if (partnershipsStore.isRequestorBootstrapperForCurrentPartnership) {
        router.push({ name: RouteNames.NoActiveRoles });
      }
    }
  }
);

onMounted(() => {
  if (userStore.email) {
    loadRequiredData();
  }
});

const suggestionsStore = useSuggestionsStore();
watch(
  () => contextSwitchStore.currentContextUid,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      suggestionsStore.loadSuggestionsForCurrentContext();
    }
  }
);

async function loadRequiredData() {
  /**
   * In case of reload, we need to load the partnerships in order to know if the current user has access to
   */
  if (typeof localStorage !== 'undefined') {
    const ctxFromStorage = localStorage.getItem('currentContextUid');
    if (ctxFromStorage) {
      await loadPartnerships();
      const part = partnershipsStore.partnerships.get(ctxFromStorage);
      if (part) {
        /**
         * If the retrieved partnerships contains the one from localStorage, we can safly set it as the current context.
         * We can also load the partnership details
         */
        contextSwitchStore.setCurrentContext(ctxFromStorage);
        partnershipsStore.getPartnershipDetails(part.uid);
      }
    }
  }

  /**
   * For ident only users, it does not have an account of it's own so we load the
   * 1st bankable partnership (ctx currently only shows bankable partnerships)
   * if none was taken from localStorage.
   */
  if (userStore.identificationOnly && !contextSwitchStore.currentContextUid) {
    if (partnershipsStore.partnerships.size === 0) {
      await loadPartnerships();
    }

    const part = partnershipsStore.getBankablePartnerships[0];
    /**
     * Due to QES onboarding, some ident only users might not have a partnership yet, therefore we skip
     * setting partnership ctx and route to QES
     */
    if (part) {
      contextSwitchStore.setCurrentContext(part.uid);
    } else {
      router.push({ name: RouteNames.AccountConfirmations });
    }
  }

  /**
   * No matter what we can prefill the userContext with the data that was already loaded.
   */
  contextSwitchStore.setUserContext(contextSwitchStore.getPrivateContext);

  if (!contextSwitchStore.currentContextUid) {
    /**
     * Only set current account as npUid if we don't already have one set (i.e. on first load).
     * In case of page reload, we may have been in another context previously and we want to keep it to that previous context.
     */
    contextSwitchStore.setCurrentContext(userStore.uid);
  }

  await loadCashAccountsForCurrentContext();
  getPendingRegistry();
}

/**
 * Load the cash-accounts for the current context and select the defaultCashAccount.
 * Regardless of the current context, we also load the private cash-account in order to enrich the user data with it's total valuation.
 */
async function loadCashAccountsForCurrentContext() {
  if (!contextSwitchStore.isPrivateContext) {
    await partnershipsStore.loadCurrentPartnershipAndCashAccounts();
  }
  cashAccountsStore
    .loadCashAccounts(
      createResourceUri(NaturalPersonURI.Base, { uid: userStore.uid, resource: NaturalPersonURI.CashAccounts })
    )
    .then(async () => {
      const privateCashAccounts = cashAccountsStore.getEntitledCashAccountsForContext(userStore.uid);
      const defaultPrivateCashAccount = privateCashAccounts[0];
      if (defaultPrivateCashAccount) {
        if (contextSwitchStore.isPrivateContext) {
          cashAccountsStore.selectCashAccount(getUidFromSelfLink(defaultPrivateCashAccount._links.self));
        }

        cashAccountsStore.getCashAccountDetails(defaultPrivateCashAccount._links.self);
      }
    })
    .catch((err) => {
      console.error('Error loading cash accounts for private context', err);
    });
}

async function loadPartnerships() {
  if (userStore.uid.length === 0) {
    return;
  }
  try {
    await partnershipsStore.loadPartnerships(
      createResourceUri(NaturalPersonURI.BaseV2, {
        uid: userStore.uid,
        resource: NaturalPersonURI.PartnershipsEngagements,
        queryParams: {
          noConstitution: true,
          partnershipStatus: [
            PartnershipStatus.New,
            PartnershipStatus.Registered,
            PartnershipStatus.Screened,
            PartnershipStatus.Identified,
            PartnershipStatus.Bankable,
            PartnershipStatus.Passive,
            PartnershipStatus.Cancelling
          ]
        }
      })
    );
    contextSwitchStore.loadPartnershipContexts();
  } catch (error) {
    console.error('Something went wrong while loading partnerships', error);
  }
}

function filterNavItems(items: Array<NavLink | NavAction>) {
  if (partnershipsStore.isRequestorBootstrapperForCurrentPartnership) {
    return items.filter(
      (item) => item.displayCondition === partnershipsStore.isRequestorBootstrapperForCurrentPartnership
    );
  }
  return items.filter((item) => {
    if ('routeName' in item) {
      const route = routes.filter((r) => r.name === item.routeName)[0];
      if (item.displayCondition === undefined) {
        return !route.meta?.contexts || route.meta?.contexts.includes(contextSwitchStore.getContextType);
      } else if (item.displayCondition === true) {
        return !route.meta?.contexts || route.meta?.contexts.includes(contextSwitchStore.getContextType);
      }
    } else {
      return item.displayCondition === undefined || item.displayCondition === true;
    }
  });
}

function filterLinks(links: Array<NavLink>) {
  return links.filter((link) => {
    const route = routes.filter((r) => r.name === link.routeName)[0];
    if (link.displayCondition === undefined) {
      return !route.meta?.contexts || route.meta?.contexts.includes(contextSwitchStore.getContextType);
    } else if (link.displayCondition === true) {
      return !route.meta?.contexts || route.meta?.contexts.includes(contextSwitchStore.getContextType);
    }
  });
}

async function getPendingRegistry() {
  if (state.currentContextSwitch && contextSwitchStore.isInternalRegistryPartnership) {
    const {
      data: { pendingCertificateUID }
    } = await apiGw.get(`/partnerships/${state.currentContextSwitch.uid}/registry/current`);

    partnershipsStore.setPendingUpdatePartnership(!!pendingCertificateUID);
  }
}

function triggerScanPaymentModal() {
  state.isScanPaymentModalOpen = true;
}
</script>
